import { BOARD_SIZE } from '../config/board.ts'
import type { TileMap } from '../config/tiles.ts'
import type { Difficulty } from '../config/ai-difficulty.ts'
import type { SessionVerification } from '../services/gameSession.ts'

// セル状態
export type Cell = 'empty' | 'black' | 'white'

// ゲームフェーズ
// title: タイトル画面 / difficulty: 難易度選択 / intro: 先手後手確定演出 /
// game: 対戦中 / finale: 勝敗確定後の演出 / result: リザルト /
// ranking: みんなのナワバリ戦ランキング画面
export type GamePhase =
  | 'title'
  | 'difficulty'
  | 'intro'
  | 'game'
  | 'finale'
  | 'result'
  | 'ranking'

// finale フェーズ内のサブステップ
// animate: 盤面のキラキラ・バウンド演出 / announce: 「○○の勝ち！」バナー
export type FinaleStep = 'animate' | 'announce'

// finale 演出の種類
// win: 5連達成 / territory: 盤面満杯時のナワバリスコア勝負
export type FinaleKind = 'win' | 'territory'

// 着手時のスコア獲得ポップアップ
export interface ScorePopup {
  id: number
  row: number
  col: number
  gain: number
  color: 'black' | 'white'
}

// 縄張りマップ（各マスが属する最大連長）
export type TerritoryMap = number[][]

// スコア内訳（両プレイヤー分）
export interface ScoreBreakdown {
  // ライン得点の内訳: {連長 → 出現数}
  lineCounts: Record<number, number>
  lineTotal: number
  clusterBonus: number
  total: number
}

// ゲーム全体状態
export interface GameState {
  board: Cell[][]
  territory: {
    black: TerritoryMap
    white: TerritoryMap
  }
  winLine: [number, number][] | null
  flowers: boolean[][]
  // 装飾用の地形タイル（見栄えのみ。ゲーム進行には影響しない）
  map: TileMap
  turn: 'black' | 'white'
  turnCount: number
  phase: GamePhase
  winner: 'black' | 'white' | null
  message: string
  // プレイヤーが担当する色。先手は常に 'black'（エンジン対称性のため不変）。
  // 'black' → プレイヤー先手・CPU 後手(+10%) / 'white' → プレイヤー後手(+10%)・CPU 先手。
  playerColor: 'black' | 'white'
  // 選択中の難易度（永続化は localStorage）
  difficulty: Difficulty
  // intro フェーズ時に表示する演出メッセージ（「あなたが先手！」等）
  introMessage: string
  // finale フェーズ専用フィールド
  finaleStep: FinaleStep | null
  finaleKind: FinaleKind | null
  finaleHighlight: [number, number][]
  // 着手のたびに発生するスコア獲得ポップアップ（アニメーション完了で除去）
  scorePopups: ScorePopup[]
  // Hub score submission に添付する任意のセッション検証データ。
  sessionVerification: SessionVerification | null
}

// 盤面の空配列を作成
export function createEmptyBoard(): Cell[][] {
  return Array.from({ length: BOARD_SIZE }, () =>
    Array<Cell>(BOARD_SIZE).fill('empty')
  )
}

export function createEmptyTerritoryMap(): TerritoryMap {
  return Array.from({ length: BOARD_SIZE }, () =>
    Array<number>(BOARD_SIZE).fill(0)
  )
}

export function createEmptyFlowers(): boolean[][] {
  return Array.from({ length: BOARD_SIZE }, () =>
    Array<boolean>(BOARD_SIZE).fill(false)
  )
}
